<template>
  <div class="space-y-6">
    <div class="mb-6">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Kontaktdaten verwalten</h2>
      <p class="text-gray-600 dark:text-gray-400">
        Bearbeiten Sie Ihre Kontaktinformationen, die auf der Startseite und Kontaktseite angezeigt werden.
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
    </div>

    <!-- Formular -->
    <form v-else @submit.prevent="handleSubmit" class="space-y-8">
      <!-- Persönliche Informationen -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Persönliche Informationen</h3>
        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.name"
              type="text"
              required
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="MAXIMILIAN REITSBERGER"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Standort <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.location"
              type="text"
              required
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="München, Deutschland"
            />
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tätigkeit <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.profession"
              type="text"
              required
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="Web-Entwicklung mit QA-Expertise"
            />
          </div>
        </div>
      </div>

      <!-- Kontaktmöglichkeiten -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Kontaktmöglichkeiten</h3>
        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              E-Mail <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.email"
              type="email"
              required
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="kontakt@example.com"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefon
            </label>
            <input
              v-model="formData.phone"
              type="tel"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="+49 123 456789"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Telefon-Anzeige
            </label>
            <input
              v-model="formData.phoneDisplay"
              type="text"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="Mo-Fr, 9-18 Uhr"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional: Text der neben der Telefonnummer angezeigt wird
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              WhatsApp
            </label>
            <input
              v-model="formData.whatsapp"
              type="url"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="https://wa.me/49123456789"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              LinkedIn
            </label>
            <input
              v-model="formData.linkedin"
              type="url"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="https://linkedin.com/in/..."
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              GitHub
            </label>
            <input
              v-model="formData.github"
              type="url"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="https://github.com/..."
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Website
            </label>
            <input
              v-model="formData.website"
              type="url"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      <!-- Erreichbarkeit -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Erreichbarkeit</h3>
        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Antwortzeit <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.responseTime"
              type="text"
              required
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="Innerhalb von 24 Stunden"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Verfügbarkeit <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.availability"
              type="text"
              required
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              placeholder="Mo-Fr, 9-18 Uhr"
            />
          </div>
        </div>
      </div>

      <!-- Benefits/Vorteile -->
      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Vorteile/Benefits</h3>
        <div class="space-y-3">
          <div
            v-for="(_, index) in formData.benefits"
            :key="index"
            class="flex items-center gap-3"
          >
            <input
              v-model="formData.benefits![index]"
              type="text"
              class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
              :placeholder="`Vorteil ${index + 1}`"
            />
            <button
              type="button"
              @click="removeBenefit(index)"
              class="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-500 transition-colors"
              :disabled="(formData.benefits?.length ?? 0) <= 1"
            >
              Entfernen
            </button>
          </div>
          <button
            type="button"
            @click="addBenefit"
            class="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-400 hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            + Vorteil hinzufügen
          </button>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex justify-end gap-4">
        <button
          type="button"
          @click="resetForm"
          class="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          :disabled="submitting"
        >
          Zurücksetzen
        </button>
        <button
          type="submit"
          class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="submitting"
        >
          <span v-if="submitting" class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Speichern...
          </span>
          <span v-else>Speichern</span>
        </button>
      </div>

      <!-- Success Message -->
      <div
        v-if="successMessage"
        class="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md text-green-800 dark:text-green-200"
      >
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div
        v-if="errorMessage"
        class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200"
      >
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { getContactInfo, updateContactInfo, getDefaultContactInfo, type ContactInfo } from '../../firebase/contact'
import { useErrorHandler } from '../../composables/useErrorHandler'

const loading = ref(true)
const submitting = ref(false)
const { handleError } = useErrorHandler()
const successMessage = ref('')
const errorMessage = ref('')

const formData = ref<Omit<ContactInfo, 'updatedAt' | 'updatedBy'>>({
  name: '',
  location: '',
  profession: '',
  email: '',
  phone: '',
  phoneDisplay: '',
  whatsapp: '',
  linkedin: '',
  github: '',
  website: '',
  responseTime: '',
  availability: '',
  benefits: []
})

const originalData = ref<Omit<ContactInfo, 'updatedAt' | 'updatedBy'> | null>(null)

const loadContactInfo = async () => {
  try {
    loading.value = true
    const contactInfo = await getContactInfo()
    
    if (contactInfo) {
      formData.value = {
        name: contactInfo.name,
        location: contactInfo.location,
        profession: contactInfo.profession,
        email: contactInfo.email,
        phone: contactInfo.phone || '',
        phoneDisplay: contactInfo.phoneDisplay || '',
        whatsapp: contactInfo.whatsapp || '',
        linkedin: contactInfo.linkedin || '',
        github: contactInfo.github || '',
        website: contactInfo.website || '',
        responseTime: contactInfo.responseTime,
        availability: contactInfo.availability,
        benefits: contactInfo.benefits || []
      }
      originalData.value = { ...formData.value }
    } else {
      // Fallback zu Standard-Werten
      const defaultData = getDefaultContactInfo()
      formData.value = {
        name: defaultData.name,
        location: defaultData.location,
        profession: defaultData.profession,
        email: defaultData.email,
        phone: defaultData.phone || '',
        phoneDisplay: defaultData.phoneDisplay || '',
        whatsapp: defaultData.whatsapp || '',
        linkedin: defaultData.linkedin || '',
        github: defaultData.github || '',
        website: defaultData.website || '',
        responseTime: defaultData.responseTime,
        availability: defaultData.availability,
        benefits: defaultData.benefits || []
      }
      originalData.value = { ...formData.value }
    }
  } catch (error) {
    const appError = await handleError(error, 'Fehler beim Laden der Kontaktdaten')
    errorMessage.value = appError.userMessage
  } finally {
    loading.value = false
  }
}

const handleSubmit = async () => {
  try {
    submitting.value = true
    successMessage.value = ''
    errorMessage.value = ''

    // Entferne leere Strings für optionale Felder
    const dataToSave: Partial<ContactInfo> = {
      name: formData.value.name,
      location: formData.value.location,
      profession: formData.value.profession,
      email: formData.value.email,
      responseTime: formData.value.responseTime,
      availability: formData.value.availability,
      benefits: (formData.value.benefits || []).filter(b => b.trim() !== '')
    }

    // Optionale Felder nur hinzufügen wenn nicht leer
    if (formData.value.phone?.trim()) {
      dataToSave.phone = formData.value.phone.trim()
    }
    if (formData.value.phoneDisplay?.trim()) {
      dataToSave.phoneDisplay = formData.value.phoneDisplay.trim()
    }
    if (formData.value.whatsapp?.trim()) {
      dataToSave.whatsapp = formData.value.whatsapp.trim()
    }
    if (formData.value.linkedin?.trim()) {
      dataToSave.linkedin = formData.value.linkedin.trim()
    }
    if (formData.value.github?.trim()) {
      dataToSave.github = formData.value.github.trim()
    }
    if (formData.value.website?.trim()) {
      dataToSave.website = formData.value.website.trim()
    }

    await updateContactInfo(dataToSave)
    
    // Lade aktualisierte Daten
    await loadContactInfo()
    
    successMessage.value = 'Kontaktdaten erfolgreich gespeichert!'
    setTimeout(() => {
      successMessage.value = ''
    }, 5000)
  } catch (error) {
    const appError = await handleError(error, 'Fehler beim Speichern der Kontaktdaten')
    errorMessage.value = appError.userMessage
  } finally {
    submitting.value = false
  }
}

const resetForm = () => {
  if (originalData.value) {
    formData.value = { ...originalData.value }
  }
  successMessage.value = ''
  errorMessage.value = ''
}

const addBenefit = () => {
  if (!formData.value.benefits) {
    formData.value.benefits = []
  }
  formData.value.benefits.push('')
}

const removeBenefit = (index: number) => {
  if (formData.value.benefits && formData.value.benefits.length > 1) {
    formData.value.benefits.splice(index, 1)
  }
}

onMounted(() => {
  loadContactInfo()
})
</script>

